import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CssBaseline,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  Drawer,
  List,
  Divider
} from '@mui/material';
import {
  Message as ChatsIcon,
  Folder as KnowledgeIcon,
  HelpOutline as HelpIcon,
  Article as DocsIcon,
  Add as AddIcon,
  ExitToApp as SignOutIcon,
  Menu as MenuIcon,
  Circle
} from '@mui/icons-material';
import { Outlet, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import authApi from '../api/authApi';
import logo from '../assets/logo.png';

const DashboardLayout = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(!isMobile && !isTablet);
  const [anchorEl, setAnchorEl] = useState(null);

  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    const handleResize = () => {
      if (isMobile || isTablet) {
        setOpen(false);
      } else {
        setOpen(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, isTablet]);

  const handleSignOut = async () => {
    try {
      await authApi.signOut();
      localStorage.clear();
      navigate('/auth', { replace: true });
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const navItems = [
    {
      icon: <ChatsIcon fontSize="medium" />,
      text: 'Chats',
      to: '/dashboard/chats'
    },
    {
      icon: <KnowledgeIcon fontSize="medium" />,
      text: 'Knowledge',
      to: '/dashboard/knowledge'
    },
    {
      icon: <HelpIcon fontSize="medium" />,
      text: 'Help',
      to: '#'
    },
    {
      icon: <DocsIcon fontSize="medium" />,
      text: 'Documentation',
      to: '#'
    },
  ];

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh',
      bgcolor: 'background.default',
    }}>
      <CssBaseline />

      {/* Sidebar */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: open ? 240 : 72,
            height: '100vh',
            borderRight: 'none',
            bgcolor: 'background.paper',
            boxShadow: theme.shadows[1],
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          },
        }}
      >
        {/* Logo Section */}
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
          <img 
            src={logo} 
            alt="Logo" 
            style={{ 
              width: open ? '140px' : '40px',
              transition: theme.transitions.create('width') 
            }} 
          />
        </Box>

        <Divider />

        {/* New Chat Button */}
        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 1,
              py: 1.2,
              letterSpacing: 0.5,
              fontWeight: 600,
              '& .MuiButton-startIcon': {
                mr: open ? 1 : 0
              }
            }}
            onClick={() => navigate('/dashboard/chats/create')}
          >
            {open && 'New Chat'}
          </Button>
        </Box>

        {/* Navigation */}
        <List sx={{ flex: 1, overflow: 'auto', px: 1 }}>
          {navItems.map((item) => (
            <Tooltip 
              key={item.text} 
              title={!open ? item.text : ''} 
              placement="right"
              arrow
            >
              <Button
                component={RouterLink}
                to={item.to}
                fullWidth
                startIcon={item.icon}
                sx={{
                  justifyContent: 'flex-start',
                  px: 2,
                  py: 1.25,
                  mb: 0.5,
                  borderRadius: 1,
                  color: location.pathname.startsWith(item.to) 
                    ? 'primary.main' 
                    : 'text.secondary',
                  bgcolor: location.pathname.startsWith(item.to)
                    ? 'action.selected'
                    : 'transparent',
                  '& .MuiButton-startIcon': {
                    mr: open ? 1.5 : 0,
                    color: 'inherit'
                  }
                }}
              >
                {open && (
                  <Typography 
                    variant="subtitle2" 
                    fontWeight={500}
                  >
                    {item.text}
                  </Typography>
                )}
              </Button>
            </Tooltip>
          ))}
        </List>

        {/* User Section */}
        <Box sx={{ p: 1.5, mx: 1, mb: 1 }}>
          <Box
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              p: 1,
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            <Avatar 
              sx={{ 
                width: 40,
                height: 40,
                bgcolor: 'primary.main',
                color: 'common.white',
              }}
            >
              {user?.Email?.charAt(0).toUpperCase()}
            </Avatar>
            {open && (
              <Box>
                <Typography 
                  variant="subtitle2" 
                  noWrap
                  fontWeight={600}
                >
                  {user?.Email}
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <Circle sx={{ 
                    fontSize: '0.5rem', 
                    mr: 0.5,
                    color: 'success.main'
                  }} />
                  Administrator
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: 2,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
              typography: 'body2'
            }
          }
        }}
      >
        <MenuItem 
          onClick={handleSignOut} 
          sx={{ color: 'error.main' }}
        >
          <SignOutIcon sx={{ mr: 1.5, fontSize: '1.25rem' }} />
          Sign Out
        </MenuItem>
      </Menu>

      {/* Main Content */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1,
          p: 3,
          width: '100%',
          marginLeft: `${open ? 240 : 72}px`,
          transition: theme.transitions.create('margin'),
          minHeight: '100vh',
          [theme.breakpoints.down('md')]: {
            marginLeft: 0,
            p: 2
          }
        }}
      >
        <IconButton
          onClick={() => setOpen(!open)}
          sx={{
            display: { md: 'none' },
            position: 'fixed',
            left: 16,
            top: 16,
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Content Container */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: 3,
            boxShadow: theme.shadows[1],
            minHeight: 'calc(100vh - 120px)',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;